import { z } from "zod";
/**
 * «Формы» (Ден 17.10.2026): our registration forms for the Academy's landings instead of GetCourse's. The Mini App of
 * the practicum owns the forms — it serves them to the landings and takes the submissions; Vekha is the constructor
 * over its ops API (plan: HypnoAnalytics/plan/forms-builder-2026-10-17/PLAN.md). The phone is always the only field.
 */
const https = (label) => z.string().trim().url(`${label}: нужна полная ссылка.`).max(2048)
    .refine(value => new URL(value).protocol === "https:", `${label}: ссылка должна начинаться с https://.`);
const HOST = /^(?=.{3,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/u;
export const SiteFormDomainSchema = z.string().trim().toLowerCase().regex(HOST, "Выберите сайт из списка.")
    .refine(value => !value.startsWith("www."), "Сайт без www.");
/** The site of a link without «www.», as the Mini App compares origins. */
export function siteFormHost(url) {
    try {
        return new URL(url).hostname.toLowerCase().replace(/^www\./u, "");
    }
    catch {
        return null;
    }
}
/**
 * A quiz (Ден 18.10.2026): questions with one tap per answer, then the phone. Each answer has points or is a STOP; the
 * person passes with the sum at least passScore and no STOP; nobody sees the points. «Спасибо» by the outcome: passed —
 * the form's thankYouUrl, failed — thankYou.failed. The Mini App counts and keeps the answers with the registration.
 */
export const SiteFormQuizOptionSchema = z.object({
    text: z.string().trim().min(1, "Нужен текст ответа.").max(200),
    points: z.number().int().min(0, "Баллы от 0.").max(100, "Баллы до 100.").default(0),
    stop: z.boolean().default(false),
}).strict();
export const SiteFormQuizQuestionSchema = z.object({
    text: z.string().trim().min(1, "Нужен текст вопроса.").max(300),
    hint: z.string().trim().max(300).default(""),
    options: z.array(SiteFormQuizOptionSchema).min(2, "У вопроса нужно хотя бы два ответа.").max(12, "Не больше 12 ответов."),
}).strict();
/**
 * The quiz's look (Влад 19.10.2026, «чтобы до конца доходило больше людей»): the strip under each question, the card of what
 * the person gets on the phone step, the window when they leave halfway. Absent — the defaults of the order (the Mini App
 * draws SITE_FORM_QUIZ_LOOK_DEFAULTS); an empty footer — no strip; *…* highlights in the footer and the intro.
 */
export const SITE_FORM_QUIZ_ICONS = ["lessons", "gift", "consult", "check"];
export const SiteFormQuizPerkSchema = z.object({ icon: z.enum(SITE_FORM_QUIZ_ICONS), text: z.string().trim().min(1, "Нужен текст строки.").max(160) }).strict();
export const SITE_FORM_QUIZ_LOOK_DEFAULTS = Object.freeze({
    footer: "После опроса откроются *3 урока по 40 минут* и *8 материалов в подарок*",
    perks: Object.freeze({ title: "Сразу после регистрации вы получите:", items: Object.freeze([
            Object.freeze({ icon: "lessons", text: "3 урока по 40 минут на нашей платформе в Telegram" }),
            Object.freeze({ icon: "gift", text: "8 практических материалов для работы и самопомощи в подарок" }),
            Object.freeze({ icon: "consult", text: "Бесплатную консультацию специалиста по итогам опроса" }),
        ]) }),
    exit: Object.freeze({ title: "Ой, погодите, не уходите", text: "Осталась ещё пара вопросов, и уроки откроются вместе с подарками" }),
});
/** The look as the site draws it: the form's own texts, else the defaults (copies: safe to edit). */
export function siteFormQuizLook(quiz) {
    const perks = quiz.perks ?? SITE_FORM_QUIZ_LOOK_DEFAULTS.perks, exit = quiz.exit ?? SITE_FORM_QUIZ_LOOK_DEFAULTS.exit;
    return { footer: quiz.footer ?? SITE_FORM_QUIZ_LOOK_DEFAULTS.footer, perks: { title: perks.title, items: perks.items.map(item => ({ ...item })) },
        exit: { title: exit.title, text: exit.text } };
}
export const SiteFormQuizSchema = z.object({
    intro: z.string().trim().max(400).default(""),
    footer: z.string().trim().max(200).optional(),
    perks: z.object({ title: z.string().trim().max(120), items: z.array(SiteFormQuizPerkSchema).max(4, "Не больше 4 строк.") }).strict().optional(),
    exit: z.object({ title: z.string().trim().min(1, "Нужен заголовок окна при выходе.").max(120), text: z.string().trim().max(240) }).strict().optional(),
    questions: z.array(SiteFormQuizQuestionSchema).min(1, "Нужен хотя бы один вопрос.").max(15, "Не больше 15 вопросов."),
    passScore: z.number().int().min(0).max(1000),
    thankYou: z.object({ failed: https("«Спасибо» для не прошедших").optional() }).strict().default({}),
}).strict();
export const SiteFormKindSchema = z.enum(["phone", "quiz", "webinar"]);
/**
 * A webinar registration (Ден 19.10.2026): the phone on the site registers the person for this webinar — one registration
 * for its whole series, as the Mini App's registration screen; «База Реги» / «Регистрация на эфир» with the caller's task at
 * once. The thank-you page's bridge (FUNNEL_PREFIX = 'ef') opens the Mini App on the webinar, the person already registered.
 */
export const SiteFormWebinarSchema = z.object({ eventId: z.string().uuid("Выберите эфир.") }).strict();
/**
 * The form's funnel (Ден 20.10.2026, one bridge): where its «Спасибо» sends the person — Практикум, Подсознание or Эфир. The
 * Mini App passes it to «Спасибо» in the address (?funnel=pz|ef) and the one bridge there opens that funnel: the block on
 * the page is the same for all. A webinar form is always «webinar».
 */
export const SITE_FORM_FUNNELS = ["practicum", "subconscious", "webinar"];
export const SiteFormFunnelSchema = z.enum(SITE_FORM_FUNNELS);
export const SITE_FORM_FUNNEL_LABELS = { practicum: "Практикум", subconscious: "Подсознание", webinar: "Эфир" };
/** The start prefix of a funnel as the older bridge v3 has it in FUNNEL_PREFIX. */
export const SITE_FORM_FUNNEL_PREFIX = { practicum: "", subconscious: "pz", webinar: "ef" };
/** The funnel of stored fields: a webinar form's is «webinar»; a Mini App before the funnels — by the kind. */
export function siteFormFunnel(fields) {
    if (fields.kind === "webinar")
        return "webinar";
    return fields.funnel === "subconscious" ? "subconscious" : "practicum";
}
/** The most a person can score and whether the threshold is reachable at all without a STOP. */
export function siteFormQuizMax(quiz) {
    return quiz.questions.reduce((sum, question) => sum + Math.max(0, ...question.options.filter(option => !option.stop).map(option => option.points)), 0);
}
export const SiteFormFieldsSchema = z.object({
    kind: SiteFormKindSchema.default("phone"),
    quiz: SiteFormQuizSchema.nullable().default(null),
    webinar: SiteFormWebinarSchema.nullable().default(null),
    funnel: SiteFormFunnelSchema.default("practicum"),
    name: z.string().trim().min(1, "Назовите форму.").max(120),
    domain: SiteFormDomainSchema,
    thankYouUrl: https("Страница «Спасибо»"),
    title: z.string().trim().min(1, "Нужен заголовок.").max(160),
    subtitle: z.string().trim().max(300).default(""),
    buttonText: z.string().trim().min(1, "Нужен текст кнопки.").max(60),
    note: z.string().trim().max(300).default(""),
    privacyUrl: https("Политика конфиденциальности"),
    ofertaUrl: https("Оферта"),
}).strict().superRefine((value, context) => {
    if (value.kind === "webinar" && !value.webinar)
        context.addIssue({ code: "custom", path: ["webinar", "eventId"], message: "Выберите эфир." });
    if (value.funnel === "webinar" && value.kind !== "webinar")
        context.addIssue({ code: "custom", path: ["funnel"], message: "Воронка «Эфир» — только у формы эфира." });
    if (value.kind !== "quiz")
        return;
    if (!value.quiz) {
        context.addIssue({ code: "custom", path: ["quiz"], message: "У квиза нужны вопросы." });
        return;
    }
    if (value.quiz.passScore > siteFormQuizMax(value.quiz))
        context.addIssue({ code: "custom", path: ["quiz", "passScore"],
            message: `Порог ${value.quiz.passScore} выше максимума ${siteFormQuizMax(value.quiz)}: квиз никто не пройдёт.` });
}).transform(value => value.kind === "webinar" ? { ...value, funnel: "webinar" } : value);
/** «Спасибо» must be on a site the Mini App accepts forms from (its origins), else the visit's marks are lost. */
export function siteFormThankYouProblem(fields, domains) {
    const urls = [fields.thankYouUrl, ...(fields.kind === "quiz" && fields.quiz?.thankYou.failed ? [fields.quiz.thankYou.failed] : [])];
    return urls.every(url => { const host = siteFormHost(url); return host && domains.includes(host); }) ? null
        : "Страница «Спасибо» должна быть на одном из подключённых сайтов.";
}
/** The texts of the GetCourse form the new one replaces (17.10.2026); links follow the chosen site. */
export const SITE_FORM_DEFAULT_TEXTS = Object.freeze({
    title: "Практикум «Профессия: делать людей счастливыми»",
    subtitle: "3 видео и 4 бонуса бесплатно. Доступ откроется сразу. Действует 72 часа",
    buttonText: "Получить практикум",
    note: "Сразу после регистрации откроется бесплатный доступ к урокам в Telegram.",
});
export function siteFormDefaultLinks(domain) {
    return { thankYouUrl: `https://${domain}/sps`, privacyUrl: `https://${domain}/privacy`, ofertaUrl: "https://academyhypnosis.com/oferta" };
}
export function siteFormDefaults(domain, name = "") {
    return { kind: "phone", quiz: null, webinar: null, funnel: "practicum", name, domain, ...SITE_FORM_DEFAULT_TEXTS, ...siteFormDefaultLinks(domain) };
}
/** A webinar form's texts and its own thank-you page: the pixels of a webinar registration, the bridge to the webinar. */
export const SITE_FORM_WEBINAR_TEXTS = Object.freeze({
    title: "Регистрация на бесплатный эфир",
    subtitle: "Оставьте номер: пришлём ссылку на эфир и напомним перед началом",
    buttonText: "Зарегистрироваться",
    note: "После регистрации откроется Telegram: там канал эфира и ссылка на трансляцию.",
});
export const siteFormWebinarThankYou = (domain) => `https://${domain}/sps-efir`;
/** A webinar form made from the webinar's card (Ден 20.10.2026, «Создать»): the same form as «Сайты → Формы → Эфир» —
 * its texts, its «Спасибо» /sps-efir — registering for the series of `eventId` (its first day). */
export function siteFormWebinarDefaults(domain, eventId, title) {
    return { ...siteFormDefaults(domain, `${domain} — эфир «${title}»`.slice(0, 120)), kind: "webinar", webinar: { eventId }, funnel: "webinar", ...SITE_FORM_WEBINAR_TEXTS,
        thankYouUrl: siteFormWebinarThankYou(domain) };
}
/** A new quiz: the phone step's texts and two empty questions to fill. */
export function siteFormQuizDefaults(domain) {
    const question = () => ({ text: "", hint: "", options: [{ text: "", points: 1, stop: false }, { text: "", points: 0, stop: false }] });
    return { intro: "", questions: [question(), question()], passScore: 1, thankYou: { failed: `https://${domain}/sps2` } };
}
export const SiteFormIdSchema = z.string().regex(/^f_[0-9a-z]{10}$/u);
export const SiteFormStateSchema = z.enum(["active", "archived"]);
const iso = z.string().datetime({ offset: true });
const count = z.number().int().nonnegative();
export const SiteFormSchema = z.object({
    id: SiteFormIdSchema, state: SiteFormStateSchema, version: z.number().int().positive(),
    // The Mini App's own validation is the stricter one; a stored form is shown as it is.
    fields: z.object({ name: z.string(), domain: z.string(), thankYouUrl: z.string(), title: z.string(), subtitle: z.string(),
        buttonText: z.string(), note: z.string(), privacyUrl: z.string(), ofertaUrl: z.string(),
        // A Mini App before the quiz has no kind: an ordinary form.
        kind: SiteFormKindSchema.catch("phone").default("phone"), quiz: SiteFormQuizSchema.nullish().catch(null).transform(value => value ?? null),
        // A Mini App before the webinar forms has none.
        webinar: SiteFormWebinarSchema.nullish().catch(null).transform(value => value ?? null),
        // A Mini App before the funnels has none: read from the kind (siteFormFunnel).
        funnel: SiteFormFunnelSchema.nullish().catch(null).transform(value => value ?? null) })
        .transform(fields => ({ ...fields, funnel: siteFormFunnel(fields) })),
    createdAt: iso, updatedAt: iso, updatedBy: z.string().nullable(), embedCode: z.string().min(1).max(4000),
});
/** Views — distinct visits that showed the form; submits — registrations; inBase — with a «База Реги» deal; inBot — came into the bot. */
/** lastTest — the last registration by a test account (not in the numbers): did it get the deal and reach the bot. */
export const SiteFormTotalsSchema = z.object({ views: count, submits: count, inBase: count, inBot: count, lastSubmitAt: iso.nullable(),
    // site — a test registration from the page (?academy_test=1, Ден 18.10.2026): kept with its answers, never sent to amo.
    lastTest: z.object({ at: iso, inBase: z.boolean(), inBot: z.boolean(), site: z.boolean().optional().default(false) }).nullable().optional() });
export const SiteFormDaySchema = z.object({ date: z.string().date(), views: count, submits: count });
/** A quiz's steps, 30 Moscow days (Влад 19.10.2026: «на каком вопросе уходят»): visits[k] — visits that saw step k (0 — the
 * first question, the last — the phone), submitted — of them registered; since — the first day the steps were counted. */
export const SiteFormStepsSchema = z.object({ since: z.string().date().nullable(), visits: z.array(count).min(1).max(16), submitted: count });
export const SiteFormStatsSchema = SiteFormTotalsSchema.extend({ days: z.array(SiteFormDaySchema).max(400),
    // A Mini App before the steps, or an ordinary form: none.
    steps: SiteFormStepsSchema.nullish().catch(null).transform(value => value ?? null) });
/** The Mini App ops API: `GET /api/ops/site-forms`. */
export const OpsSiteFormListSchema = z.object({
    forms: z.array(SiteFormSchema.extend({ stats: SiteFormTotalsSchema })).max(1000),
    domains: z.array(z.string()).max(200),
});
/** `GET /api/ops/site-forms/:id`. */
/** The form's latest registrations (tests too) with the quiz's answers and outcome; phone — its last 4 digits. */
export const SiteFormRegistrationSchema = z.object({
    id: z.string().min(1).max(80), at: iso, isTest: z.boolean(), phoneTail: z.string().max(8), crmLeadId: z.string().max(20).nullable(), inBot: z.boolean(),
    quiz: z.object({ score: z.number().int(), passed: z.boolean(),
        answers: z.array(z.object({ question: z.string(), answer: z.string(), points: z.number().int().default(0), stop: z.boolean().default(false) })).max(30) }).nullable(),
});
export const OpsSiteFormDetailSchema = z.object({ form: SiteFormSchema, stats: SiteFormStatsSchema,
    recent: z.array(SiteFormRegistrationSchema).max(100).default([]) });
/** Who changed a form, as the Mini App stores it. */
export const SiteFormActorSchema = z.object({ telegramId: z.string().regex(/^[1-9]\d{0,19}$/u).optional(), name: z.string().min(1).max(120) }).strict();
/** Vekha's API, `/api/site-forms`. */
export const SiteFormSourceStateSchema = z.enum(["ok", "not_configured", "unavailable"]);
export const SiteFormsOverviewSchema = z.object({
    source: SiteFormSourceStateSchema, canEdit: z.boolean(), checkedAt: iso,
    forms: OpsSiteFormListSchema.shape.forms, domains: z.array(z.string()),
});
export const SiteFormDetailSchema = OpsSiteFormDetailSchema.extend({ canEdit: z.boolean(), domains: z.array(z.string()) });
export const SiteFormCreateSchema = z.object({ fields: SiteFormFieldsSchema }).strict();
export const SiteFormUpdateSchema = z.object({ fields: SiteFormFieldsSchema, expectedVersion: z.number().int().positive() }).strict();
export const SiteFormStateMutationSchema = z.object({ state: SiteFormStateSchema }).strict();
export const SiteFormMutationResultSchema = z.object({ form: SiteFormSchema });
/** «Подключить новый сайт» (Ден 17.10.2026): the editor connects a landing's site without a server change. Any form of the
 * address; the Mini App takes its host without «www.» and connects https://host and https://www.host. */
export const SiteFormDomainAddSchema = z.object({ domain: z.string().trim().min(3, "Введите адрес сайта.").max(2048) }).strict();
/** `POST /api/ops/site-forms/domains`: collector — the landing has the marks collector (journey attribution v4 + Google Ads). */
export const OpsSiteFormDomainAddedSchema = z.object({
    domain: z.string().min(1), domains: z.array(z.string()).max(200),
    check: z.object({ reachable: z.boolean(), collector: z.boolean() }), alreadyConnected: z.boolean(),
});
/**
 * «Сайты» (Ден 17.10.2026: «переделаем вкладку не формы, а сайты»): the connected sites with what stands on their pages,
 * whether our systems see them and what is missing; the forms below. The Mini App checks the pages (GET only, hourly);
 * contract — HypnoAnalytics/plan/forms-builder-2026-10-17/PLAN.md, «Вкладка «Сайты»».
 */
export const SITE_PROBLEM_CODES = ["no_collector", "collector_duplicate", "no_offline_route_with_clicks", "no_offline_route", "no_traffic_30d",
    "page_unreachable", "thanks_no_bridge", "thanks_delay_not_7s", "thanks_funnel_mismatch", "thanks_old_bridge", "gc_form", "our_form_not_on_page", "our_form_wrong", "no_form"];
export const SiteProblemSchema = z.object({ code: z.string().min(1).max(80), severity: z.enum(["error", "warning"]), text: z.string().min(1).max(500) });
export const SitePageSchema = z.object({
    url: z.string().min(1).max(2048), kind: z.enum(["landing", "thanks"]), checkedAt: iso.nullable(), status: z.number().int().nullable(),
    collector: z.boolean(), registrationHash: z.string().max(200).nullable(),
    form: z.object({ kind: z.enum(["gc", "ours", "none"]), ids: z.array(z.string().max(80)).max(20),
        /** Which of our forms (Ден 18.10.2026): ordinary, quiz, or not the right one. A Mini App before it omits the list. */
        ours: z.array(z.object({ id: z.string().max(80), kind: z.enum(["phone", "quiz", "webinar", "archived", "other_site", "unknown"]).catch("unknown"),
            name: z.string().max(200).nullable() })).max(20).optional() }),
    /** version 4 — the one bridge, 3 — the older one with its FUNNEL_PREFIX; a Mini App before the one bridge omits it. */
    bridge: z.object({ present: z.boolean(), delayMs: z.number().int().nonnegative().nullable(), funnelPrefix: z.string().max(40).nullable(),
        version: z.union([z.literal(3), z.literal(4)]).nullish().catch(null).transform(value => value ?? null) }).nullable(),
    visits30: count,
    /** «Заглушить страницу» (Ден 17.10.2026: «и конкретные страницы тоже»): no problems, no effect on the site's state. */
    muted: z.object({ at: iso, by: z.string().max(200) }).nullish().transform(value => value ?? null),
});
export const SiteSchema = z.object({
    host: z.string().min(1).max(253), source: z.enum(["env", "vekha"]), addedAt: iso.nullable(),
    stats: z.object({ visits30: count, googleClicks30: count, registrations30: count, lastVisitAt: iso.nullable() }),
    offline: z.object({ account: z.string().max(120).nullable() }),
    pages: z.array(SitePageSchema).max(60), problems: z.array(SiteProblemSchema).max(60),
    /** «Заглушить сайт» (Ден 17.10.2026: перестали вести рекламу): still connected, out of the tiles and the highlights. A Mini
     * App before the switch omits it: null. */
    muted: z.object({ at: iso, by: z.string().max(200) }).nullish().transform(value => value ?? null),
});
/** `GET /api/ops/sites` and `POST /api/ops/sites/check`. */
export const OpsSitesSchema = z.object({ checkedAt: iso.nullable(), sites: z.array(SiteSchema).max(300) });
export const SiteCheckRequestSchema = z.object({ host: z.string().trim().min(3).max(253).optional() }).strict();
/** `POST /api/sites/mute` (Vekha) and `POST /api/ops/sites/mute` (Mini App, plus the actor): mute or bring back a site. */
export const SiteMuteRequestSchema = z.object({ host: z.string().trim().min(3).max(253),
    /** A page of the site («/pril»); without it — the whole site. */
    path: z.string().trim().regex(/^\/[^\s?#]{0,300}$/u).optional(), muted: z.boolean() }).strict();
/** Vekha's `/api/sites`: not_released — the Mini App has no site checks yet (404); the sites then come from the forms' list. */
export const SitesOverviewSchema = z.object({
    source: z.enum(["ok", "not_released", "not_configured", "unavailable"]), canEdit: z.boolean(), checkedAt: iso.nullable(),
    sites: z.array(SiteSchema).max(300),
});
/** Sites known only by name (before the Mini App checks them): no pages, no numbers, nothing claimed missing. */
export function bareSite(host) {
    return { host, source: "env", addedAt: null, stats: { visits30: 0, googleClicks30: 0, registrations30: 0, lastVisitAt: null },
        offline: { account: null }, pages: [], problems: [], muted: null };
}
/** How to put a landing, its form and its «Спасибо» on a site, per funnel, as the Mini App has it (`GET /api/ops/site-guide`,
 * Ден 20.10.2026; the same text is public at MINI_APP_ORIGIN/site-guide): «Сайты → Инструкции» shows it, never a copy. */
const GuideStepSchema = z.object({ key: z.enum(["landing", "form", "thanks"]), title: z.string().max(200), hint: z.string().max(300),
    text: z.string().max(4000), code: z.string().min(1).max(40_000) });
export const OpsSiteGuideSchema = z.object({ funnels: z.array(z.object({ key: z.string().min(1).max(40), label: z.string().max(80), prefix: z.string().max(20),
        forms: z.array(z.string().max(20)).max(5), steps: z.array(GuideStepSchema).max(6) })).min(1).max(10), check: z.string().max(1000),
    /** The one bridge (20.10.2026): the three steps of every funnel and how the funnel is chosen; before it — by funnel only. */
    steps: z.array(GuideStepSchema).max(6).optional(), funnel: z.string().max(1000).nullish() })
    .transform(guide => ({ ...guide, steps: guide.steps ?? guide.funnels[0].steps, funnel: guide.funnel ?? null }));
/** Vekha's `GET /api/sites/guide`: not_released — a Mini App before the endpoint. */
export const SiteGuideSchema = z.object({ source: z.enum(["ok", "not_released", "not_configured", "unavailable"]), guide: OpsSiteGuideSchema.nullable() });
//# sourceMappingURL=site-forms.js.map